/**
 * Up to four whole digits and two decimals, with either decimal separator.
 *
 * These are the shapes a person passes through while typing a percentage, so
 * "8." and ".5" are allowed even though neither is a finished number. There is
 * no sign: a rate here is never negative.
 */
const PERCENT_TEXT = /^\d{0,4}(?:[.,]\d{0,2})?$/;

/** The largest rate the text rule above can hold: 9999.99%. */
const MAX_BASIS_POINTS = 999_999;

/**
 * Whether text is a percentage, or a prefix of one, that a field may hold.
 *
 * @param text what the field would contain
 * @returns true when it has at most two decimals and no stray characters
 */
export function isPercentText(text: string): boolean {
  return PERCENT_TEXT.test(text);
}

/**
 * Shows basis points as the percentage a person would write.
 *
 * Integer arithmetic throughout, so no float ever decides how a rate reads.
 * Trailing zeros are dropped: 800 is "8", 850 is "8.5", 825 is "8.25".
 *
 * Only a rate that {@link percentTextToBasisPoints} would read back is shown, so
 * a field is never left holding text that cannot be typed again. A value below
 * zero, above 9999.99% or not a number has no such text and shows as nothing.
 *
 * @param basisPoints hundredths of a percent, or null
 * @returns the percentage without a percent sign, or an empty string for null or a value no field can hold
 */
export function basisPointsToPercentText(basisPoints: number | null): string {
  if (basisPoints === null || !Number.isFinite(basisPoints)) {
    return '';
  }
  const rounded = Math.round(basisPoints);
  if (rounded < 0 || rounded > MAX_BASIS_POINTS) {
    return '';
  }
  const whole = String(Math.floor(rounded / 100));
  const hundredths = rounded % 100;
  if (hundredths === 0) {
    return whole;
  }
  const fraction = String(hundredths).padStart(2, '0').replace(/0$/, '');
  return `${whole}.${fraction}`;
}

/**
 * Reads typed text as basis points.
 *
 * @param text what the person typed, for example "8.25" or "8,25"
 * @returns whole basis points (825), or null when the text is empty or not a percentage
 */
export function percentTextToBasisPoints(text: string): number | null {
  const normalised = text.trim().replace(',', '.');
  if (normalised === '' || normalised === '.' || !isPercentText(normalised)) {
    return null;
  }
  const [whole = '', fraction = ''] = normalised.split('.');
  return Number(whole === '' ? '0' : whole) * 100 + Number(fraction.padEnd(2, '0'));
}
